import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { useWindowStore } from '@/store/windowStore';
import { useBackStore } from '@/lib/backNavigation';

// --- Корректное закрытие ВЛОЖЕННЫХ модальных окон ---------------------------
// Каждое окно пушит фейковую запись истории (window-system), а при закрытии
// делает history.go(-1). Для ВЛОЖЕННОГО окна (напр. пикер поверх формы) этот
// go(-1) вызывает popstate у РОДИТЕЛЬСКОГО окна, чья метка hydraModal снова
// совпадает → родитель закрывался тоже («выбрал исполнителя — закрылись все
// окна»). Подавляем РОВНО один popstate на каждое закрытие, ЕСЛИ остаются
// другие открытые модалки (родители).
const openModalIds = new Set<string>();

interface UniversalWindowProps {
  open: boolean;
  onClose: () => void;
  title: string;
  blocking?: boolean;
  width?: string;
  height?: string;
  topBar?: React.ReactNode;
  leftBar?: React.ReactNode;  // основные кнопки слева (Провести, Сохранить)
  bottomBar?: React.ReactNode;
  children: React.ReactNode;
  hideCloseButton?: boolean;
  /** Если false — рендерится как inline-контент (встраивается в страницу) */
  modal?: boolean;
  /** Показывать кнопку «Расширить/Сузить» (мелким диалогам не нужна) */
  resizable?: boolean;
  /** Начальное состояние (восстановление из хранилища настроек) */
  initialMaximized?: boolean;
  initialPos?: { x: number; y: number };
  /** Изменение геометрии окна (для сохранения в настройках) */
  onGeometryChange?: (g: { maximized: boolean; pos: { x: number; y: number } }) => void;
}

export function UniversalWindow({
  open, onClose, title, blocking = false, width = 'w-[min(760px,94vw)]', height,
  topBar, leftBar, bottomBar, children, hideCloseButton, modal = true,
  resizable = true,
  initialMaximized, initialPos, onGeometryChange,
}: UniversalWindowProps) {
  const [pos, setPos] = useState(initialPos || { x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(!!initialMaximized);
  const dragOffset = useRef({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement>(null);
  // Реактивно: vw/vh-размеры подстраиваются при изменении окна приложения
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const openWindow = useWindowStore((s) => s.openWindow);
  const closeWindow = useWindowStore((s) => s.closeWindow);

  // SSoT «Назад»: стабильный id окна + свежий onClose для общего стека.
  const windowIdRef = useRef<string>('');
  if (windowIdRef.current === '') windowIdRef.current = Math.random().toString(36).slice(2);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const registerWindow = useBackStore((s) => s.registerWindow);
  const unregisterWindow = useBackStore((s) => s.unregisterWindow);

  // Модальные окна — в общий стек «Назад» (окно → родитель). Inline-формы
  // (state-экраны) в стек не входят: их «назад» = закрыть текущий экран.
  useEffect(() => {
    if (!open || !modal) return;
    const id = windowIdRef.current;
    registerWindow(id, () => closeRef.current());
    return () => unregisterWindow(id);
  }, [open, modal, registerWindow, unregisterWindow]);

  useEffect(() => {
    if (open) {
      setPos(initialPos || { x: 0, y: 0 });
      setDragging(false);
      setMinimized(false);
      setMaximized(!!initialMaximized);
      // Для не-modal режима — обновляем useWindowStore для отображения topBar
      if (!modal && topBar) {
        useWindowStore.setState({ title, topBar });
      }
    } else {
      if (useWindowStore.getState().title === title) closeWindow();
    }
  }, [open, title, modal, topBar, initialMaximized, initialPos]);

  // Сообщаем об изменении геометрии (для хранилища настроек)
  useEffect(() => {
    if (!open || !onGeometryChange) return;
    onGeometryChange({ maximized, pos });
  }, [maximized, pos, open, onGeometryChange]);

  // Закрытие по ESC
  useEffect(() => {
    if (!open || !blocking) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, blocking, onClose]);

  // СИСТЕМНАЯ «НАЗАД» (мобильные/браузер): модалки открыты через state (не history),
  // поэтому системная кнопка «назад» неактивна. Пушим фейковую запись истории при
  // открытии окна; popstate (нажатие «назад») → закрываем окно. Универсально для
  // ВСЕХ окон/пикеров (SRP — одна точка).
  useEffect(() => {
    if (!open) return;
    const stateId = Math.random().toString(36).slice(2);
    // Историю (фейковая запись + «назад» закрывает окно) ведёт ТОЛЬКО КОРНЕВОЕ
    // модальное окно. Вложенные (пикер поверх формы, форма из «+ Создать»)
    // НЕ трогают history — иначе go(-1) при закрытии вложенного вызывал popstate
    // у родителя и закрывал ВСЕ окна («выбрал исполнителя — закрылись все окна»).
    const isRoot = openModalIds.size === 0;
    openModalIds.add(stateId);
    if (!isRoot) {
      return () => { openModalIds.delete(stateId); };
    }
    window.history.pushState({ hydraModal: stateId }, '');
    const handlePop = () => {
      // popstate на нашей записи = системная «назад» → закрыть модалку
      if (window.history.state?.hydraModal === stateId) {
        onClose();
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => {
      window.removeEventListener('popstate', handlePop);
      openModalIds.delete(stateId);
      // При закрытии другим способом (кнопка/выбор) — убираем нашу запись истории
      try {
        if (window.history.state?.hydraModal === stateId) {
          window.history.go(-1);
        }
      } catch { /* история недоступна — игнор */ }
    };
  }, [open, onClose]);

  // Глобальное закрытие всех модалок (например, переход из StatusBar
  // на страницу очереди синхронизации)
  useEffect(() => {
    if (!open || !modal) return;
    const handler = () => onClose();
    window.addEventListener('hydra:close-modals', handler);
    return () => window.removeEventListener('hydra:close-modals', handler);
  }, [open, modal, onClose]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input, select, textarea, a')) return;
    if (maximized) return;
    const rect = modalRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
  }, [maximized]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging) return;
    setPos({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y,
    });
  }, [dragging]);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  if (!open) return null;

  // Inline mode (state-экран: форма в рабочей области, НЕ поверх) — обычный
  // flex-макет с шапкой/экшен-панелью/контентом; без портала/оверлея/drag.
  if (!modal) {
    return (
      <div className="flex flex-col flex-1 min-h-0 h-full bg-background">
        {/* Title bar — компактный (без drag) */}
        <div className="flex items-center gap-1 px-2 py-1 border-b bg-muted/20 shrink-0">
          {!hideCloseButton && <BackButton onClick={onClose} />}
          <span className="flex-1 min-w-0 text-xs font-medium truncate pr-2">{title}</span>
          <div className="flex items-center gap-1 shrink-0">
            {resizable && (
              <button type="button" onClick={() => setMaximized((v) => !v)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground" title={maximized ? 'Сузить' : 'Расширить'}>
                {maximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
            )}
            {!hideCloseButton && (
              <button type="button" onClick={onClose} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground" title="Закрыть">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {/* Экшен-панель (Сохранить/Отмена/вкладки) */}
        {topBar && <div className="shrink-0 border-b bg-muted/10">{topBar}</div>}
        {/* Контент — занимает свободное место, скролл внутри */}
        {!minimized && <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>}
        {bottomBar && <div className="shrink-0 border-t bg-muted/20 text-[10px] text-muted-foreground px-3 py-1">{bottomBar}</div>}
      </div>
    );
  }

  // Modal mode — на весь экран.
  // Рендерим в портал document.body: иначе вложенная модалка рендерится внутри
  // родительской, а у той от анимации остаётся CSS-transform → transform создаёт
  // containing block → position:fixed вложенной модалки считается от родителя,
  // и каждая следующая модалка открывается всё ниже. В портале все модалки —
  // дети body, fixed работает от окна, все открываются в одной позиции.
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[15000]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {blocking && <motion.div className="absolute inset-0 bg-black/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} />}

          <motion.div
            ref={modalRef}
            className={isMobile
              ? 'fixed inset-0 z-[15001] flex flex-col bg-background pointer-events-auto overflow-hidden pb-[env(safe-area-inset-bottom)]'
              : `absolute ${maximized ? 'inset-0' : width.includes('w-') ? width : `w-full ${width}`} ${maximized || height === 'auto' ? '' : height || 'h-[min(76vh,92vh)]'} flex flex-col bg-background ${maximized ? '' : 'border rounded-lg shadow-2xl'} pointer-events-auto overflow-hidden`
            }
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={isMobile || maximized ? {} : {
              left: pos.x || '50%',
              top: pos.y || '50%',
              // Центр экрана — статичный transform (не анимируется; framer при
              // prefers-reduced-motion НЕ применяет transform-анимации):
              // при drag transform убирается, позиция — через left/top.
              transform: pos.x === 0 && pos.y === 0 ? 'translate(-50%, -50%)' : 'none',
              ...(height ? { height } : {}),
            }}
            onClick={(e) => e.stopPropagation()}
          >
        {/* Title bar */}
        <div
          className="flex items-center gap-1 px-2 py-1.5 border-b bg-muted/20 shrink-0 cursor-move select-none"
          onMouseDown={handleMouseDown}
        >
          {!hideCloseButton && <BackButton onClick={onClose} />}
          <span className="flex-1 min-w-0 text-xs font-medium truncate pr-2">{title}</span>
          <div className="flex items-center gap-1 shrink-0">
            {/* Расширить / сузить (как в Windows) — только для resizable-окон */}
            {resizable && (
              <button
                type="button"
                onClick={() => setMaximized((v) => !v)}
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                title={maximized ? 'Сузить' : 'Расширить'}
              >
                {maximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
            )}
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                title="Закрыть"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Top bar — action panel */}
        {topBar && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-muted/20 shrink-0">
            {topBar}
          </div>
        )}

        {/* Content — скролл ВСЕГО окна (включая вкладки/подчёркивания контента);
            collapsible when minimized */}
        {!minimized && (
          <div className="flex-1 overflow-y-auto p-4">
            {children}
          </div>
        )}

        {/* Мобильный fullscreen: крупная нижняя кнопка «Закрыть» — иначе
            модалку (форма выбора и т.п.) невозможно закрыть без выбора
            (нижняя системная «назад» модалку не трогает). */}
        {isMobile && !hideCloseButton && (
          <div className="shrink-0 border-t bg-muted/20 p-2 pb-[env(safe-area-inset-bottom)]">
            <Button size="sm" variant="outline" className="w-full" onClick={onClose}>
              <X className="h-3.5 w-3.5 mr-1" /> Закрыть
            </Button>
          </div>
        )}

        {/* Bottom bar — info/status */}
        {bottomBar && !minimized && (
          <div className="flex items-center justify-between px-3 py-1.5 border-t bg-muted/20 text-[10px] text-muted-foreground shrink-0">
            {bottomBar}
          </div>
        )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
